import { FormEvent, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscriptionApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type SetupPendingData = Awaited<
  ReturnType<typeof subscriptionApi.getSetupPending>
>["data"];

type Enrollment = SetupPendingData["enrollments"][number];

function formatMoney(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function errorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    const e = error as { error?: string; message?: string };
    if (typeof e.error === "string" && e.error.trim()) return e.error;
    if (typeof e.message === "string" && e.message.trim()) return e.message;
  }
  if (error instanceof Error) return error.message;
  return "Request failed";
}

export default function SubscriptionSetupPage() {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [data, setData] = useState<SetupPendingData | null>(null);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string>("");
  const [paymentMethodId, setPaymentMethodId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [lastResult, setLastResult] = useState<{
    squareSubscriptionId: string;
    startDate: string;
  } | null>(null);

  const selectedEnrollment: Enrollment | undefined = data?.enrollments.find(
    (e) => e.localSubscriptionId === selectedEnrollmentId
  );

  const runSearch = async (termOverride?: string) => {
    const term = (termOverride ?? search).trim();
    if (!term) {
      toast.error("Enter a student email");
      return;
    }

    setLoading(true);
    setLastResult(null);
    try {
      const looksLikeObjectId = /^[a-f\d]{24}$/i.test(term);
      const res = await subscriptionApi.getSetupPending(
        looksLikeObjectId ? { studentId: term } : { email: term }
      );
      setData(res.data);
      const first = res.data.enrollments[0];
      setSelectedEnrollmentId(first?.localSubscriptionId || "");
      setStartDate(first?.suggestedStartDate || "");
      const defaultCard =
        res.data.paymentMethods.find((c) => c.isDefault) || res.data.paymentMethods[0];
      setPaymentMethodId(defaultCard?.id || "");

      if (res.data.enrollments.length === 0) {
        toast.message("No setup-pending subscriptions for this student");
      }
    } catch (error) {
      setData(null);
      setSelectedEnrollmentId("");
      setPaymentMethodId("");
      setStartDate("");
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const onSelectEnrollment = (enrollmentId: string) => {
    setSelectedEnrollmentId(enrollmentId);
    setLastResult(null);
    const enrollment = data?.enrollments.find((e) => e.localSubscriptionId === enrollmentId);
    if (enrollment) {
      setStartDate(enrollment.suggestedStartDate);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedEnrollment) {
      toast.error("Select a setup-pending enrollment");
      return;
    }
    if (!paymentMethodId) {
      toast.error("Ask the student to add a card via Payments → Update Card first");
      return;
    }
    if (!startDate) {
      toast.error("Start date is required");
      return;
    }

    setSubmittingId(selectedEnrollment.localSubscriptionId);
    try {
      const res = await subscriptionApi.completeSquareSetup(
        selectedEnrollment.localSubscriptionId,
        { paymentMethodId, startDate }
      );
      setLastResult({
        squareSubscriptionId: res.data.squareSubscriptionId,
        startDate: res.data.startDate,
      });
      toast.success(res.message || "Square subscription linked");
      await runSearch(data?.student.email || search);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Subscription Setup
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Complete Square billing for enrollments that paid the first installment but never got a
          Square subscription (for example orphan Apple Pay checkouts). Uses a card the student
          already saved — no Square Dashboard needed.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Find student</CardTitle>
          <CardDescription>Search by exact email or student Mongo ID.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="student-search">Email or student ID</Label>
              <Input
                id="student-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="student@email.com"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void runSearch();
                  }
                }}
              />
            </div>
            <Button type="button" onClick={() => void runSearch()} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}

      {data ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>
                {data.student.firstName} {data.student.lastName}
              </CardTitle>
              <CardDescription>
                {data.student.email}
                {data.student.phone ? ` · ${data.student.phone}` : ""}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Setup-pending enrollments:{" "}
              <span className="font-medium text-foreground">{data.enrollments.length}</span>
              {" · "}
              Saved cards:{" "}
              <span className="font-medium text-foreground">{data.paymentMethods.length}</span>
            </CardContent>
          </Card>

          {data.enrollments.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No setup-pending subscriptions found for this student.
              </CardContent>
            </Card>
          ) : (
            <form onSubmit={onSubmit} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Enrollment</CardTitle>
                  <CardDescription>
                    Choose the local subscription that still needs a Square subscription.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Setup-pending enrollment</Label>
                    <Select value={selectedEnrollmentId} onValueChange={onSelectEnrollment}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select enrollment" />
                      </SelectTrigger>
                      <SelectContent>
                        {data.enrollments.map((enrollment) => (
                          <SelectItem
                            key={enrollment.localSubscriptionId}
                            value={enrollment.localSubscriptionId}
                          >
                            {enrollment.programName} · {enrollment.planName} ·{" "}
                            {enrollment.completedPayments}/{enrollment.numberOfPayments} paid
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedEnrollment ? (
                    <div className="grid gap-3 rounded-lg border border-border bg-muted/30 p-4 text-sm sm:grid-cols-2">
                      <div>
                        <div className="text-muted-foreground">Program</div>
                        <div className="font-medium">{selectedEnrollment.programName}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Plan</div>
                        <div className="font-medium">{selectedEnrollment.planName}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Installments</div>
                        <div className="font-medium">
                          {selectedEnrollment.completedPayments} paid ·{" "}
                          {selectedEnrollment.remainingPayments} remaining of{" "}
                          {selectedEnrollment.numberOfPayments}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Amount</div>
                        <div className="font-medium">
                          {formatMoney(selectedEnrollment.amountPerInstallment)} / installment
                          <span className="text-muted-foreground">
                            {" "}
                            · {formatMoney(selectedEnrollment.amountRemaining)} left
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Local subscription</div>
                        <div className="font-mono text-xs">{selectedEnrollment.localSubscriptionId}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Next due (local)</div>
                        <div className="font-medium">
                          {selectedEnrollment.nextPaymentDate || "—"}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Billing setup</CardTitle>
                  <CardDescription>
                    Square will start charging on the start date. Installment #1 is already paid —
                    do not use today’s date if that would double-charge.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {data.paymentMethods.length === 0 ? (
                    <div
                      className={cn(
                        "rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
                      )}
                    >
                      No active saved cards. Ask the student to open the student portal → Payments →{" "}
                      <strong>Update Card</strong>, then search again.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Saved card</Label>
                      <Select value={paymentMethodId} onValueChange={setPaymentMethodId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select card" />
                        </SelectTrigger>
                        <SelectContent>
                          {data.paymentMethods.map((card) => (
                            <SelectItem key={card.id} value={card.id}>
                              {card.cardBrand} ····{card.lastFour}
                              {card.isDefault ? " (default)" : ""} · exp {card.expiryDate}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="start-date">Square subscription start date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Prefills from the next local installment due date when available.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={
                      !selectedEnrollment ||
                      !paymentMethodId ||
                      !startDate ||
                      submittingId !== null
                    }
                  >
                    {submittingId ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Create Square subscription &amp; link
                  </Button>
                </CardContent>
              </Card>
            </form>
          )}

          {lastResult ? (
            <Card>
              <CardHeader>
                <CardTitle>Success</CardTitle>
                <CardDescription>
                  Square subscription is active. If a Dashboard recurring invoice (e.g. 6950) was
                  created earlier for this student, void it to avoid double billing.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 font-mono text-sm">
                <div>squareSubscriptionId: {lastResult.squareSubscriptionId}</div>
                <div>startDate: {lastResult.startDate}</div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
